import { useState, useMemo, useEffect } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  GraduationCap,
  FileText,
  ClipboardCheck,
  Eye,
  Scale,
  Info,
  Building,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getStateRequirements } from '../../config/stateRequirements';

// ============================================
// TYPE DEFINITIONS
// ============================================

type QuestionType = 'number' | 'single' | 'multi' | 'yes_no';
type ComplianceStatus = 'COMPLIANT' | 'AT_RISK' | 'NON_COMPLIANT' | 'UNKNOWN' | 'NOT_APPLICABLE';

interface QuestionOption {
  value: string;
  label: string;
}

interface StateRequirement {
  threshold?: number;
  thresholdType?: 'min' | 'max' | 'exact';
  requiredValue?: string | string[];
  requiredIncludes?: string[];
  citation: string;
  description: string;
  nonCompliantIf?: 'below' | 'above' | 'notEquals' | 'notIncludes';
  atRiskIf?: 'below' | 'above' | 'notEquals' | 'notIncludes';
  atRiskThreshold?: number;
}

interface DiagnosticQuestion {
  id: string;
  section: string;
  question: string;
  extractionPrompt: string;
  type: QuestionType;
  options?: QuestionOption[];
  placeholder?: string;
  unit?: string;

  // State visibility
  universal?: boolean;                    // true = shown to all states
  applicableStates?: string[];            // Only these states see this question
  excludedStates?: string[];              // These states do NOT see this question
  noLicenseOnly?: boolean;                // Only show for AL, MI, ID
  licensedOnly?: boolean;                 // Only show for states requiring license

  // State-specific requirements
  requirements: Record<string, StateRequirement>;
  defaultRequirement?: StateRequirement;

  // Conditional display based on other answers
  showIf?: {
    questionId: string;
    value: string | string[];
  };

  // Skip remaining questions in section
  skipSectionIf?: {
    value: string | string[];
  };
}

interface DiagnosticSection {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  questions: DiagnosticQuestion[];
  // Section-level visibility
  excludedStates?: string[];
  noLicenseOnly?: boolean;
  licensedOnly?: boolean;
}

interface EvaluationResult {
  status: ComplianceStatus;
  userValue: string | number | string[] | null;
  requiredValue: string | number | string[] | null;
  gap?: string;
  citation?: string;
  description?: string;
}

// ============================================
// CONSTANTS
// ============================================

const NO_LICENSE_STATES = new Set(['AL', 'MI', 'ID']);

// ============================================
// STATE-SPECIFIC REQUIREMENTS DATA
// ============================================

// Initial training requirements by state
const INITIAL_TRAINING_REQUIREMENTS: Record<string, StateRequirement> = {
  CA: {
    threshold: 40,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: 'HSC §1796.63',
    description: 'Home care aides must complete 40 hours of training',
  },
  VA: {
    threshold: 40,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '12VAC5-381-100',
    description: 'Personal care aides require 40 hours initial training',
  },
  NY: {
    threshold: 75,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '10 NYCRR 766.11',
    description: 'Home health aides must complete 75-hour training program',
  },
  TX: {
    threshold: 75,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '26 TAC §558.259',
    description: 'Home health aides must complete 75 hours of training',
  },
  FL: {
    threshold: 75,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: 'F.S. §400.476',
    description: 'Home health aides require 75-hour training program',
  },
  PA: {
    threshold: 75,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '28 Pa. Code §611.55',
    description: 'Direct care workers must complete 75 hours of training',
  },
  IL: {
    threshold: 120,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '77 Ill. Adm. Code 245.40',
    description: 'Home health aides require 120 hours of training',
  },
  NC: {
    threshold: 80,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '10A NCAC 13J .1103',
    description: 'Home care aides must complete 80-hour training program',
  },
  DEFAULT: {
    threshold: 40,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    atRiskThreshold: 75,
    atRiskIf: 'below',
    citation: 'State regulations vary',
    description: 'Most states require 40-75 hours of initial training',
  },
};

// Annual in-service training requirements
const ANNUAL_TRAINING_REQUIREMENTS: Record<string, StateRequirement> = {
  CA: {
    threshold: 5,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: 'HSC §1796.63(c)',
    description: '5 hours of annual in-service training required',
  },
  VA: {
    threshold: 12,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '12VAC5-381-100(E)',
    description: '12 hours of annual in-service training required',
  },
  NY: {
    threshold: 12,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: '10 NYCRR 766.11(c)',
    description: '12 hours of annual in-service required',
  },
  FL: {
    threshold: 12,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: 'F.S. §400.476(2)',
    description: '12 hours of annual continuing education required',
  },
  DEFAULT: {
    threshold: 12,
    thresholdType: 'min',
    nonCompliantIf: 'below',
    citation: 'Medicare CoPs 42 CFR §484.80(d)',
    description: 'Medicare requires 12 hours annual in-service (federal minimum)',
  },
};

// Background check requirements
const BACKGROUND_CHECK_TYPE_REQUIREMENTS: Record<string, StateRequirement> = {
  CA: {
    requiredValue: 'state_fbi',
    nonCompliantIf: 'notEquals',
    citation: 'HSC §1736.5',
    description: 'California requires DOJ and FBI Live Scan fingerprint check',
  },
  NY: {
    requiredValue: 'state_fbi',
    nonCompliantIf: 'notEquals',
    citation: 'PHL §2899-a',
    description: 'New York requires state and FBI fingerprint check',
  },
  TX: {
    requiredValue: 'state_fbi',
    nonCompliantIf: 'notEquals',
    citation: 'HSC §250.003',
    description: 'Texas requires name-based and FBI fingerprint check',
  },
  FL: {
    requiredValue: 'state_fbi',
    nonCompliantIf: 'notEquals',
    citation: 'F.S. §408.809',
    description: 'Florida requires Level 2 (state + FBI fingerprint) screening',
  },
  VA: {
    requiredValue: ['state_only', 'state_fbi'],
    atRiskIf: 'notIncludes',
    citation: 'Va. Code §32.1-162.9:1',
    description: 'Virginia requires state criminal background check',
  },
  DEFAULT: {
    requiredValue: 'state_only',
    atRiskIf: 'notEquals',
    citation: 'State regulations vary',
    description: 'Most states require at minimum a state criminal background check',
  },
};

// Supervision frequency requirements (in days)
const SUPERVISION_REQUIREMENTS: Record<string, StateRequirement> = {
  CA: {
    threshold: 14,
    thresholdType: 'max',
    nonCompliantIf: 'above',
    citation: 'CCR Title 22 §74723',
    description: 'Supervisory visits required every 14 days',
  },
  NY: {
    threshold: 14,
    thresholdType: 'max',
    nonCompliantIf: 'above',
    citation: '10 NYCRR 763.13',
    description: 'Supervisory visits required every 14 days for skilled services',
  },
  VA: {
    threshold: 60,
    thresholdType: 'max',
    nonCompliantIf: 'above',
    citation: '12VAC5-381-150',
    description: 'Supervisory visits required every 60 days',
  },
  DEFAULT: {
    threshold: 60,
    thresholdType: 'max',
    nonCompliantIf: 'above',
    atRiskThreshold: 30,
    atRiskIf: 'above',
    citation: '42 CFR §484.80(h)',
    description: 'Medicare requires HHA supervision at least every 60 days',
  },
};

// ============================================
// STATE-SPECIFIC SECTION GENERATOR
// ============================================

/**
 * Generate state-specific diagnostic sections based on the selected state
 */
function generateStateSpecificSections(stateCode: string): DiagnosticSection[] {
  const stateReqs = getStateRequirements(stateCode);

  if (!stateReqs) {
    // Fallback to generic sections if state not found
    return DIAGNOSTIC_SECTIONS_FALLBACK;
  }

  const sections: DiagnosticSection[] = [];

  // ==========================================
  // SECTION 1: LICENSING (if state requires license)
  // ==========================================
  if (stateReqs.requiresLicense) {
    sections.push({
      id: 'licensing',
      title: 'State Licensing',
      description: `${stateReqs.stateName} licensing requirements via ${stateReqs.licensingAgencyAbbr}`,
      icon: Building,
      questions: [
        {
          id: 'license_current',
          section: 'licensing',
          question: `Do you have a current, valid ${stateReqs.licenseType}?`,
          extractionPrompt: 'Current state license status',
          type: 'yes_no',
          universal: true,
          requirements: {
            [stateCode]: {
              requiredValue: 'yes',
              nonCompliantIf: 'notEquals',
              citation: stateReqs.licenseCitation || 'State licensing regulations',
              description: `${stateReqs.stateName} requires a ${stateReqs.licenseType} from ${stateReqs.licensingAgencyAbbr}`,
            },
          },
        },
        {
          id: 'license_expiration_tracked',
          section: 'licensing',
          question: 'Do you track your license expiration date and renew before it expires?',
          extractionPrompt: 'License expiration tracking',
          type: 'yes_no',
          universal: true,
          showIf: { questionId: 'license_current', value: 'yes' },
          requirements: {
            DEFAULT: {
              requiredValue: 'yes',
              atRiskIf: 'notEquals',
              citation: stateReqs.licenseCitation || 'Best practice',
              description: 'License expiration should be tracked to ensure continuous compliance',
            },
          },
        },
        {
          id: 'license_displayed',
          section: 'licensing',
          question: `Is your ${stateReqs.licensingAgencyAbbr} license prominently displayed at your principal place of business?`,
          extractionPrompt: 'License display status',
          type: 'yes_no',
          universal: true,
          showIf: { questionId: 'license_current', value: 'yes' },
          requirements: {
            DEFAULT: {
              requiredValue: 'yes',
              atRiskIf: 'notEquals',
              citation: stateReqs.licenseCitation || 'State regulations',
              description: 'Most states require license to be displayed',
            },
          },
        },
      ],
    });
  } else {
    // No license required - add federal certification section
    sections.push({
      id: 'federal_certification',
      title: 'Federal Certification',
      description: `${stateReqs.stateName} does not require state licensure; federal certification may apply`,
      icon: FileText,
      questions: [
        {
          id: 'medicare_certified',
          section: 'federal_certification',
          question: 'Is your agency Medicare certified?',
          extractionPrompt: 'Medicare certification status',
          type: 'yes_no',
          universal: true,
          requirements: {
            DEFAULT: {
              requiredValue: ['yes', 'no'],
              citation: 'CMS CoPs',
              description: 'Medicare certification is optional but required for Medicare reimbursement',
            },
          },
        },
        {
          id: 'medicare_conditions',
          section: 'federal_certification',
          question: 'Do you comply with Medicare Conditions of Participation (CoPs)?',
          extractionPrompt: 'Medicare CoPs compliance',
          type: 'yes_no',
          universal: true,
          showIf: { questionId: 'medicare_certified', value: 'yes' },
          requirements: {
            DEFAULT: {
              requiredValue: 'yes',
              nonCompliantIf: 'notEquals',
              citation: '42 CFR Part 484',
              description: 'Medicare certified agencies must comply with CoPs',
            },
          },
        },
      ],
    });
  }

  // ==========================================
  // SECTION 2: BACKGROUND CHECKS
  // ==========================================
  const requiredBgChecks = stateReqs.backgroundChecks.filter(bc => bc.required);
  const bgCheckOptions = stateReqs.backgroundChecks.map(bc => ({
    value: bc.id,
    label: bc.label,
  }));
  const requiredBgCheckIds = requiredBgChecks.map(bc => bc.id);

  sections.push({
    id: 'background_screening',
    title: 'Background Check Requirements',
    description: `${stateReqs.stateName} requires background checks per ${stateReqs.backgroundCheckCitation}`,
    icon: Shield,
    questions: [
      {
        id: 'bg_checks_conducted',
        section: 'background_screening',
        question: `For ALL employees, contractors, and volunteers providing direct care, which background checks do you conduct? (Select all that apply)`,
        extractionPrompt: 'Background checks conducted',
        type: 'multi',
        universal: true,
        options: bgCheckOptions,
        requirements: {
          [stateCode]: {
            requiredIncludes: requiredBgCheckIds,
            citation: stateReqs.backgroundCheckCitation,
            description: `${stateReqs.stateName} requires: ${requiredBgChecks.map(bc => bc.label).join(', ')}`,
          },
        },
      },
      {
        id: 'bg_checks_timing',
        section: 'background_screening',
        question: 'Are background checks completed BEFORE employees have patient contact?',
        extractionPrompt: 'Background check timing',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: stateReqs.backgroundCheckCitation,
            description: 'Background checks must be completed before direct care begins',
          },
        },
      },
      {
        id: 'bg_substantiated_abuse',
        section: 'background_screening',
        question: 'Do you verify that NO employee has a substantiated record of abuse, neglect, or exploitation?',
        extractionPrompt: 'Abuse record verification',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: stateReqs.abuseRegistryCitation || stateReqs.backgroundCheckCitation,
            description: `Cannot employ anyone with substantiated record in ${stateReqs.abuseRegistryName}`,
          },
        },
      },
    ],
  });

  // Add fingerprint question if required
  if (stateReqs.fingerprintRequired) {
    const bgSection = sections.find(s => s.id === 'background_screening');
    if (bgSection) {
      bgSection.questions.push({
        id: 'fingerprint_check',
        section: 'background_screening',
        question: `Do you conduct ${stateReqs.fingerprintType} fingerprint-based background checks?`,
        extractionPrompt: 'Fingerprint background check',
        type: 'yes_no',
        universal: true,
        requirements: {
          [stateCode]: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: stateReqs.backgroundCheckCitation,
            description: `${stateReqs.stateName} requires ${stateReqs.fingerprintType} fingerprinting`,
          },
        },
      });
    }
  }

  // ==========================================
  // SECTION 3: TRAINING
  // ==========================================
  sections.push({
    id: 'training',
    title: 'Caregiver Training',
    description: `${stateReqs.stateName} training requirements per ${stateReqs.trainingCitation}`,
    icon: GraduationCap,
    questions: [
      {
        id: 'initial_training_hours',
        section: 'training',
        question: `How many hours of initial training do your home care aides complete before providing care?`,
        extractionPrompt: 'Initial training hours',
        type: 'number',
        placeholder: 'Enter hours',
        unit: 'hours',
        universal: true,
        requirements: {
          [stateCode]: {
            threshold: stateReqs.initialTrainingHours,
            thresholdType: 'min',
            nonCompliantIf: 'below',
            citation: stateReqs.trainingCitation,
            description: `${stateReqs.stateName} requires ${stateReqs.initialTrainingHours} hours of initial training`,
          },
        },
      },
      {
        id: 'annual_training_hours',
        section: 'training',
        question: `How many hours of annual in-service training do your caregivers complete?`,
        extractionPrompt: 'Annual training hours',
        type: 'number',
        placeholder: 'Enter hours per year',
        unit: 'hours/year',
        universal: true,
        requirements: {
          [stateCode]: {
            threshold: stateReqs.annualTrainingHours,
            thresholdType: 'min',
            nonCompliantIf: 'below',
            citation: stateReqs.trainingCitation,
            description: `${stateReqs.stateName} requires ${stateReqs.annualTrainingHours} hours of annual in-service training`,
          },
        },
      },
      {
        id: 'training_documented',
        section: 'training',
        question: 'Is training completion documented in each employee\'s personnel file?',
        extractionPrompt: 'Training documentation',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: stateReqs.trainingCitation,
            description: 'Training records must be maintained for each employee',
          },
        },
      },
      {
        id: 'competency_evaluation',
        section: 'training',
        question: 'Do you conduct competency evaluations for all caregivers?',
        extractionPrompt: 'Competency evaluations conducted',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: stateReqs.trainingCitation,
            description: 'Competency evaluations are recommended to verify training effectiveness',
          },
        },
      },
    ],
  });

  // ==========================================
  // SECTION 4: SUPERVISION
  // ==========================================
  sections.push({
    id: 'supervision',
    title: 'Supervision & Oversight',
    description: `${stateReqs.stateName} supervision requirements per ${stateReqs.supervisionCitation}`,
    icon: Eye,
    questions: [
      {
        id: 'supervision_frequency',
        section: 'supervision',
        question: `How often does a qualified supervisor conduct on-site supervisory visits?`,
        extractionPrompt: 'Supervision visit frequency',
        type: 'single',
        universal: true,
        options: [
          { value: '14', label: 'Every 2 weeks' },
          { value: '30', label: 'Monthly' },
          { value: '60', label: 'Every 60 days' },
          { value: '90', label: 'Quarterly' },
          { value: '180', label: 'Every 6 months' },
          { value: '365', label: 'Annually or less frequently' },
        ],
        requirements: {
          [stateCode]: {
            threshold: stateReqs.supervisionFrequencyDays,
            thresholdType: 'max',
            nonCompliantIf: 'above',
            citation: stateReqs.supervisionCitation,
            description: `${stateReqs.stateName} requires supervisory visits at least every ${stateReqs.supervisionFrequencyDays} days`,
          },
        },
      },
      {
        id: 'supervision_documented',
        section: 'supervision',
        question: 'Are supervisory visits documented with date, findings, and follow-up actions?',
        extractionPrompt: 'Supervision documentation',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: stateReqs.supervisionCitation,
            description: 'Supervisory visits must be documented',
          },
        },
      },
      {
        id: 'care_plan_review',
        section: 'supervision',
        question: 'Are client care plans reviewed and updated at least annually?',
        extractionPrompt: 'Care plan review frequency',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: 'Best practice',
            description: 'Care plans should be reviewed at least annually or with significant changes',
          },
        },
      },
    ],
  });

  // ==========================================
  // SECTION 5: DOCUMENTATION & RECORDS
  // ==========================================
  sections.push({
    id: 'documentation',
    title: 'Documentation & Records',
    description: 'Record keeping and documentation compliance',
    icon: FileText,
    questions: [
      {
        id: 'personnel_files_complete',
        section: 'documentation',
        question: 'Do ALL personnel files contain: photo ID, background check results, training records, and signed job description?',
        extractionPrompt: 'Personnel file completeness',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Personnel files must contain all required documentation',
          },
        },
      },
      {
        id: 'client_records_maintained',
        section: 'documentation',
        question: 'Are client records maintained with care plans, service notes, and assessments?',
        extractionPrompt: 'Client record maintenance',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Complete client records are required',
          },
        },
      },
      {
        id: 'hipaa_compliance',
        section: 'documentation',
        question: 'Do you have HIPAA policies and conduct annual privacy training?',
        extractionPrompt: 'HIPAA compliance',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: '45 CFR Parts 160-164',
            description: 'HIPAA compliance is required for all healthcare entities',
          },
        },
      },
    ],
  });

  return sections;
}

// Fallback sections for unknown states
const DIAGNOSTIC_SECTIONS_FALLBACK: DiagnosticSection[] = [
  // ==========================================
  // SECTION 1: LICENSING (Licensed States Only)
  // ==========================================
  {
    id: 'licensing',
    title: 'State Licensing',
    description: 'License status and compliance',
    icon: Building,
    licensedOnly: true, // Only for states requiring license
    questions: [
      {
        id: 'license_current',
        section: 'licensing',
        question: 'Do you have a current, valid state home care license?',
        extractionPrompt: 'Current state license status',
        type: 'yes_no',
        licensedOnly: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Operating without a valid license is prohibited',
          },
        },
      },
      {
        id: 'license_expiration_tracked',
        section: 'licensing',
        question: 'Do you track your license expiration date and renew before it expires?',
        extractionPrompt: 'License expiration tracking',
        type: 'yes_no',
        licensedOnly: true,
        showIf: { questionId: 'license_current', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: 'Best practice',
            description: 'License expiration should be tracked to ensure continuous compliance',
          },
        },
      },
      // California-specific
      {
        id: 'ca_hcsb_registered',
        section: 'licensing',
        question: 'Is your agency registered with the California Home Care Services Bureau (HCSB)?',
        extractionPrompt: 'HCSB registration status',
        type: 'yes_no',
        applicableStates: ['CA'],
        requirements: {
          CA: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'HSC §1796.10',
            description: 'All home care organizations must register with HCSB',
          },
        },
      },
      {
        id: 'ca_hca_registration',
        section: 'licensing',
        question: 'Do all your home care aides have current HCA registration with HCSB?',
        extractionPrompt: 'HCA registration status for all aides',
        type: 'single',
        applicableStates: ['CA'],
        options: [
          { value: 'all', label: 'Yes, all aides are registered' },
          { value: 'some', label: 'Some aides are registered' },
          { value: 'none', label: 'No aides are registered' },
        ],
        requirements: {
          CA: {
            requiredValue: 'all',
            nonCompliantIf: 'notEquals',
            citation: 'HSC §1796.16',
            description: 'All home care aides must be registered with HCSB before providing services',
          },
        },
      },
      // New York-specific
      {
        id: 'ny_lhcsa_license',
        section: 'licensing',
        question: 'Do you hold a valid LHCSA (Licensed Home Care Services Agency) license?',
        extractionPrompt: 'LHCSA license status',
        type: 'yes_no',
        applicableStates: ['NY'],
        requirements: {
          NY: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'PHL Article 36',
            description: 'LHCSA license is required to operate a home care agency in New York',
          },
        },
      },
      // Florida-specific
      {
        id: 'fl_ahca_license_type',
        section: 'licensing',
        question: 'What type of AHCA license does your agency hold?',
        extractionPrompt: 'AHCA license type',
        type: 'single',
        applicableStates: ['FL'],
        options: [
          { value: 'hha', label: 'Home Health Agency (HHA)' },
          { value: 'nurse_registry', label: 'Nurse Registry' },
          { value: 'homemaker', label: 'Homemaker/Companion' },
          { value: 'none', label: 'No AHCA license' },
        ],
        requirements: {
          FL: {
            requiredValue: ['hha', 'nurse_registry', 'homemaker'],
            nonCompliantIf: 'notIncludes',
            citation: 'F.S. Chapter 400',
            description: 'AHCA licensure is required to operate a home care agency in Florida',
          },
        },
      },
      // Texas-specific
      {
        id: 'tx_hhsc_license',
        section: 'licensing',
        question: 'Are you licensed by the Texas Health and Human Services Commission (HHSC)?',
        extractionPrompt: 'HHSC license status',
        type: 'yes_no',
        applicableStates: ['TX'],
        requirements: {
          TX: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: '26 TAC Chapter 558',
            description: 'HHSC licensure is required for home health agencies in Texas',
          },
        },
      },
    ],
  },

  // ==========================================
  // SECTION 1B: MEDICARE CERTIFICATION (No-License States)
  // ==========================================
  {
    id: 'certification',
    title: 'Federal Certification',
    description: 'Medicare and Medicaid certification status',
    icon: Shield,
    noLicenseOnly: true, // Only for AL, MI, ID
    questions: [
      {
        id: 'medicare_certified',
        section: 'certification',
        question: 'Is your agency Medicare certified (CMS-certified)?',
        extractionPrompt: 'Medicare certification status',
        type: 'yes_no',
        noLicenseOnly: true,
        requirements: {
          AL: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: '42 CFR Part 484',
            description: 'Alabama has no state license; Medicare certification provides federal oversight',
          },
          MI: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: '42 CFR Part 484',
            description: 'Michigan has no state license for non-medical; Medicare cert required for skilled services',
          },
          ID: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: '42 CFR Part 484',
            description: 'Idaho eliminated state license (7/1/2025); Medicare certification still required for skilled',
          },
        },
      },
      {
        id: 'medicare_survey_ready',
        section: 'certification',
        question: 'Are you prepared for a Medicare recertification survey?',
        extractionPrompt: 'Medicare survey readiness',
        type: 'yes_no',
        noLicenseOnly: true,
        showIf: { questionId: 'medicare_certified', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: '42 CFR §484.22',
            description: 'Medicare-certified agencies must maintain continuous compliance for surveys',
          },
        },
      },
    ],
  },

  // ==========================================
  // SECTION 2: BACKGROUND SCREENING
  // ==========================================
  {
    id: 'background_screening',
    title: 'Background Screening',
    description: 'Employee background check compliance',
    icon: Shield,
    questions: [
      {
        id: 'bg_check_conducted',
        section: 'background_screening',
        question: 'Do you conduct background checks on all direct care employees before they have client contact?',
        extractionPrompt: 'Background checks completed before client contact',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Background checks required before employees provide direct care',
          },
        },
        skipSectionIf: { value: 'no' },
      },
      {
        id: 'bg_check_type',
        section: 'background_screening',
        question: 'What type of criminal background check do you conduct?',
        extractionPrompt: 'Type of background check conducted',
        type: 'single',
        universal: true,
        options: [
          { value: 'state_only', label: 'State criminal check only' },
          { value: 'state_fbi', label: 'State + FBI fingerprint check' },
          { value: 'name_national', label: 'Name-based national check' },
        ],
        showIf: { questionId: 'bg_check_conducted', value: 'yes' },
        requirements: BACKGROUND_CHECK_TYPE_REQUIREMENTS,
        defaultRequirement: BACKGROUND_CHECK_TYPE_REQUIREMENTS.DEFAULT,
      },
      // California-specific: Live Scan
      {
        id: 'ca_live_scan',
        section: 'background_screening',
        question: 'Do you use DOJ and FBI Live Scan fingerprinting for all employees?',
        extractionPrompt: 'Live Scan fingerprinting used',
        type: 'yes_no',
        applicableStates: ['CA'],
        showIf: { questionId: 'bg_check_conducted', value: 'yes' },
        requirements: {
          CA: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'HSC §1736.5',
            description: 'California requires Live Scan fingerprinting through DOJ and FBI',
          },
        },
      },
      // Florida-specific: Level 2 screening
      {
        id: 'fl_level2_screening',
        section: 'background_screening',
        question: 'Do you conduct Level 2 background screening (state + FBI fingerprint)?',
        extractionPrompt: 'Level 2 screening conducted',
        type: 'yes_no',
        applicableStates: ['FL'],
        showIf: { questionId: 'bg_check_conducted', value: 'yes' },
        requirements: {
          FL: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'F.S. §408.809',
            description: 'Florida requires Level 2 (fingerprint) background screening',
          },
        },
      },
      // Universal: Abuse registry
      {
        id: 'bg_abuse_registry',
        section: 'background_screening',
        question: 'Do you check the state abuse/neglect registry before hire?',
        extractionPrompt: 'Abuse registry checked',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'bg_check_conducted', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Abuse registry checks are required before hire in most states',
          },
        },
      },
      // Universal: OIG check
      {
        id: 'bg_oig_check',
        section: 'background_screening',
        question: 'Do you check the OIG exclusion list before hire?',
        extractionPrompt: 'OIG exclusion list checked',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'bg_check_conducted', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: '42 CFR §1001.1901',
            description: 'Must verify employees are not excluded from federal programs',
          },
        },
      },
      // Universal: Re-screening frequency
      {
        id: 'bg_rescreen_months',
        section: 'background_screening',
        question: 'How often do you re-screen existing employees for OIG exclusions?',
        extractionPrompt: 'OIG re-screening frequency',
        type: 'single',
        universal: true,
        options: [
          { value: '1', label: 'Monthly' },
          { value: '3', label: 'Quarterly' },
          { value: '6', label: 'Every 6 months' },
          { value: '12', label: 'Annually' },
          { value: '0', label: 'Never / Only at hire' },
        ],
        showIf: { questionId: 'bg_check_conducted', value: 'yes' },
        requirements: {
          DEFAULT: {
            threshold: 12,
            thresholdType: 'max',
            nonCompliantIf: 'above',
            atRiskThreshold: 1,
            atRiskIf: 'above',
            citation: 'OIG Compliance Guidance',
            description: 'OIG recommends monthly checks; annual is minimum',
          },
        },
      },
    ],
  },

  // ==========================================
  // SECTION 3: TRAINING
  // ==========================================
  {
    id: 'training',
    title: 'Caregiver Training',
    description: 'Training program compliance',
    icon: GraduationCap,
    questions: [
      {
        id: 'training_program_exists',
        section: 'training',
        question: 'Do you have a documented training program for caregivers?',
        extractionPrompt: 'Documented training program exists',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Documented training programs are required',
          },
        },
        skipSectionIf: { value: 'no' },
      },
      {
        id: 'training_initial_hours',
        section: 'training',
        question: 'How many hours of initial training do caregivers complete before first client contact?',
        extractionPrompt: 'Initial training hours',
        type: 'number',
        placeholder: 'Enter hours',
        unit: 'hours',
        universal: true,
        showIf: { questionId: 'training_program_exists', value: 'yes' },
        requirements: INITIAL_TRAINING_REQUIREMENTS,
        defaultRequirement: INITIAL_TRAINING_REQUIREMENTS.DEFAULT,
      },
      {
        id: 'training_annual_hours',
        section: 'training',
        question: 'How many hours of annual in-service training do caregivers complete?',
        extractionPrompt: 'Annual in-service hours',
        type: 'number',
        placeholder: 'Enter hours',
        unit: 'hours',
        universal: true,
        showIf: { questionId: 'training_program_exists', value: 'yes' },
        requirements: ANNUAL_TRAINING_REQUIREMENTS,
        defaultRequirement: ANNUAL_TRAINING_REQUIREMENTS.DEFAULT,
      },
      {
        id: 'training_abuse_covered',
        section: 'training',
        question: 'Does your training include abuse/neglect recognition and mandatory reporting?',
        extractionPrompt: 'Abuse/neglect training included',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'training_program_exists', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State mandatory reporter laws',
            description: 'All caregivers must be trained on abuse/neglect recognition',
          },
        },
      },
      {
        id: 'training_infection_control',
        section: 'training',
        question: 'Does your training include infection control and prevention?',
        extractionPrompt: 'Infection control training included',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'training_program_exists', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: '42 CFR §484.70',
            description: 'Infection control training is required for all direct care staff',
          },
        },
      },
      {
        id: 'training_documentation',
        section: 'training',
        question: 'Do you maintain documented proof of training completion for each employee?',
        extractionPrompt: 'Training documentation maintained',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'training_program_exists', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Training completion must be documented for survey review',
          },
        },
      },
      {
        id: 'training_competency_eval',
        section: 'training',
        question: 'Do you conduct competency evaluations (skills testing) for caregivers?',
        extractionPrompt: 'Competency evaluations conducted',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'training_program_exists', value: 'yes' },
        requirements: {
          CA: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'HSC §1796.63(b)',
            description: 'Competency evaluation required for home care aides in CA',
          },
          DEFAULT: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: '42 CFR §484.80(c)',
            description: 'Competency evaluation required for HHAs; recommended for all',
          },
        },
      },
    ],
  },

  // ==========================================
  // SECTION 4: SUPERVISION
  // ==========================================
  {
    id: 'supervision',
    title: 'Caregiver Supervision',
    description: 'Supervisory visit compliance',
    icon: Eye,
    questions: [
      {
        id: 'supervision_conducted',
        section: 'supervision',
        question: 'Do you conduct supervisory visits for caregivers in client homes?',
        extractionPrompt: 'Supervisory visits conducted',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Supervisory visits are required for licensed agencies',
          },
        },
        skipSectionIf: { value: 'no' },
      },
      {
        id: 'supervision_frequency_days',
        section: 'supervision',
        question: 'What is the maximum number of days between supervisory visits?',
        extractionPrompt: 'Max days between supervisory visits',
        type: 'number',
        placeholder: 'Enter days',
        unit: 'days',
        universal: true,
        showIf: { questionId: 'supervision_conducted', value: 'yes' },
        requirements: SUPERVISION_REQUIREMENTS,
        defaultRequirement: SUPERVISION_REQUIREMENTS.DEFAULT,
      },
      {
        id: 'supervision_by_rn',
        section: 'supervision',
        question: 'Are supervisory visits for skilled services conducted by an RN?',
        extractionPrompt: 'RN conducts skilled service supervision',
        type: 'single',
        universal: true,
        options: [
          { value: 'yes', label: 'Yes, always by RN' },
          { value: 'sometimes', label: 'Sometimes RN, sometimes other' },
          { value: 'no', label: 'No, not by RN' },
          { value: 'no_skilled', label: "We don't provide skilled services" },
        ],
        showIf: { questionId: 'supervision_conducted', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: ['yes', 'no_skilled'],
            nonCompliantIf: 'notIncludes',
            citation: '42 CFR §484.80(h)',
            description: 'RN must supervise HHA services for Medicare agencies',
          },
        },
      },
      {
        id: 'supervision_documented',
        section: 'supervision',
        question: 'Do you document supervisory visits with a standardized form?',
        extractionPrompt: 'Supervisory visits documented',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'supervision_conducted', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Supervisory visits must be documented and retained',
          },
        },
      },
    ],
  },

  // ==========================================
  // SECTION 5: PERSONNEL RECORDS
  // ==========================================
  {
    id: 'personnel',
    title: 'Personnel Records',
    description: 'Employee file compliance',
    icon: FileText,
    questions: [
      {
        id: 'personnel_files_maintained',
        section: 'personnel',
        question: 'Do you maintain a personnel file for each employee?',
        extractionPrompt: 'Personnel files maintained',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Personnel files are required for all employees',
          },
        },
        skipSectionIf: { value: 'no' },
      },
      {
        id: 'personnel_bg_on_file',
        section: 'personnel',
        question: 'Are background check results documented in each personnel file?',
        extractionPrompt: 'Background checks in personnel files',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'personnel_files_maintained', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Background check documentation must be in personnel file',
          },
        },
      },
      {
        id: 'personnel_training_on_file',
        section: 'personnel',
        question: 'Are training records documented in each personnel file?',
        extractionPrompt: 'Training records in personnel files',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'personnel_files_maintained', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Training completion must be in personnel file',
          },
        },
      },
      {
        id: 'personnel_health_screen',
        section: 'personnel',
        question: 'Do you have health screening (including TB test) results on file for each caregiver?',
        extractionPrompt: 'Health screening/TB test on file',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'personnel_files_maintained', value: 'yes' },
        requirements: {
          CA: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'HSC §1725.5',
            description: 'TB test and health screening required for direct care staff',
          },
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Health screening required for direct care staff in most states',
          },
        },
      },
      {
        id: 'personnel_expiration_tracking',
        section: 'personnel',
        question: 'Do you have a system to track expiration dates (licenses, certifications, TB tests)?',
        extractionPrompt: 'Expiration tracking system exists',
        type: 'yes_no',
        universal: true,
        showIf: { questionId: 'personnel_files_maintained', value: 'yes' },
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            atRiskIf: 'notEquals',
            citation: 'Best practice',
            description: 'Expiration tracking prevents employees working with expired credentials',
          },
        },
      },
    ],
  },

  // ==========================================
  // SECTION 6: CARE DELIVERY
  // ==========================================
  {
    id: 'care_delivery',
    title: 'Care Delivery',
    description: 'Care plan and documentation compliance',
    icon: ClipboardCheck,
    questions: [
      {
        id: 'care_plan_exists',
        section: 'care_delivery',
        question: 'Do you have an individualized care plan for every client before services begin?',
        extractionPrompt: 'Care plans exist for all clients',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Individualized care plans are required before services begin',
          },
        },
        skipSectionIf: { value: 'no' },
      },
      {
        id: 'care_plan_review_days',
        section: 'care_delivery',
        question: 'How often do you review and update care plans (in days)?',
        extractionPrompt: 'Care plan review frequency',
        type: 'number',
        placeholder: 'Enter days',
        unit: 'days',
        universal: true,
        showIf: { questionId: 'care_plan_exists', value: 'yes' },
        requirements: {
          CA: {
            threshold: 365,
            thresholdType: 'max',
            nonCompliantIf: 'above',
            atRiskThreshold: 90,
            atRiskIf: 'above',
            citation: 'CCR Title 22 §74717',
            description: 'Care plans must be reviewed at least annually; quarterly is best practice',
          },
          DEFAULT: {
            threshold: 60,
            thresholdType: 'max',
            nonCompliantIf: 'above',
            citation: '42 CFR §484.60',
            description: 'Medicare requires care plan review every 60 days',
          },
        },
      },
      {
        id: 'evv_used',
        section: 'care_delivery',
        question: 'Do you use Electronic Visit Verification (EVV) for Medicaid personal care services?',
        extractionPrompt: 'EVV used for Medicaid services',
        type: 'single',
        universal: true,
        options: [
          { value: 'yes', label: 'Yes, for all Medicaid services' },
          { value: 'partial', label: 'For some services only' },
          { value: 'no', label: 'No EVV' },
          { value: 'no_medicaid', label: "We don't serve Medicaid clients" },
        ],
        requirements: {
          DEFAULT: {
            requiredValue: ['yes', 'no_medicaid'],
            nonCompliantIf: 'notIncludes',
            citation: '21st Century Cures Act §12006',
            description: 'EVV is federally mandated for Medicaid personal care services',
          },
        },
      },
      {
        id: 'service_documentation',
        section: 'care_delivery',
        question: 'Do caregivers document services provided after each visit?',
        extractionPrompt: 'Service documentation completed each visit',
        type: 'yes_no',
        universal: true,
        requirements: {
          DEFAULT: {
            requiredValue: 'yes',
            nonCompliantIf: 'notEquals',
            citation: 'State licensing regulations',
            description: 'Service documentation is required for each visit',
          },
        },
      },
    ],
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

function isQuestionVisible(
  question: DiagnosticQuestion,
  stateCode: string,
  answers: Record<string, string | string[] | number | null>
): boolean {
  const isNoLicenseState = NO_LICENSE_STATES.has(stateCode);

  // Check state-specific visibility
  if (question.noLicenseOnly && !isNoLicenseState) return false;
  if (question.licensedOnly && isNoLicenseState) return false;
  if (question.applicableStates && !question.applicableStates.includes(stateCode)) return false;
  if (question.excludedStates && question.excludedStates.includes(stateCode)) return false;

  // Check showIf condition
  if (question.showIf) {
    const conditionValues = Array.isArray(question.showIf.value)
      ? question.showIf.value
      : [question.showIf.value];
    if (!conditionValues.includes(answers[question.showIf.questionId] as string)) {
      return false;
    }
  }

  return true;
}

function isSectionVisible(section: DiagnosticSection, stateCode: string): boolean {
  const isNoLicenseState = NO_LICENSE_STATES.has(stateCode);

  if (section.noLicenseOnly && !isNoLicenseState) return false;
  if (section.licensedOnly && isNoLicenseState) return false;
  if (section.excludedStates && section.excludedStates.includes(stateCode)) return false;

  return true;
}

function evaluateQuestion(
  question: DiagnosticQuestion,
  answer: string | number | string[] | null,
  stateCode: string
): EvaluationResult {
  if (answer === null || answer === undefined || answer === '') {
    return { status: 'UNKNOWN', userValue: null, requiredValue: null };
  }

  const requirement = question.requirements[stateCode] ||
    question.requirements['DEFAULT'] ||
    question.defaultRequirement;

  if (!requirement) {
    return { status: 'UNKNOWN', userValue: answer, requiredValue: null };
  }

  let status: ComplianceStatus = 'COMPLIANT';
  let gap: string | undefined;

  // Handle number comparisons
  if (question.type === 'number' && requirement.threshold !== undefined) {
    const numValue = typeof answer === 'number' ? answer : parseFloat(answer as string);

    if (isNaN(numValue)) {
      return {
        status: 'UNKNOWN',
        userValue: answer,
        requiredValue: requirement.threshold,
        citation: requirement.citation,
        description: requirement.description,
      };
    }

    if (requirement.thresholdType === 'min') {
      if (requirement.nonCompliantIf === 'below' && numValue < requirement.threshold) {
        status = 'NON_COMPLIANT';
        gap = `Your agency: ${numValue} ${question.unit || ''} | Required: ${requirement.threshold}+ ${question.unit || ''}`;
      } else if (requirement.atRiskIf === 'below' && requirement.atRiskThreshold && numValue < requirement.atRiskThreshold) {
        status = 'AT_RISK';
        gap = `Your agency: ${numValue} ${question.unit || ''} | Recommended: ${requirement.atRiskThreshold}+ ${question.unit || ''}`;
      }
    } else if (requirement.thresholdType === 'max') {
      if (requirement.nonCompliantIf === 'above' && numValue > requirement.threshold) {
        status = 'NON_COMPLIANT';
        gap = `Your agency: ${numValue} ${question.unit || ''} | Required: ${requirement.threshold} ${question.unit || ''} or less`;
      } else if (requirement.atRiskIf === 'above' && requirement.atRiskThreshold && numValue > requirement.atRiskThreshold) {
        status = 'AT_RISK';
        gap = `Your agency: ${numValue} ${question.unit || ''} | Recommended: ${requirement.atRiskThreshold} ${question.unit || ''} or less`;
      }
    }

    return {
      status,
      userValue: numValue,
      requiredValue: requirement.threshold,
      gap,
      citation: requirement.citation,
      description: requirement.description,
    };
  }

  // Handle yes/no and single select
  if (question.type === 'yes_no' || question.type === 'single') {
    const strValue = answer as string;
    const requiredValues = Array.isArray(requirement.requiredValue)
      ? requirement.requiredValue
      : requirement.requiredValue ? [requirement.requiredValue] : [];

    if (requirement.nonCompliantIf === 'notEquals' && !requiredValues.includes(strValue)) {
      status = 'NON_COMPLIANT';
      const userLabel = strValue === 'yes' ? 'Yes' : strValue === 'no' ? 'No' : strValue;
      gap = `Your answer: ${userLabel} | Required: ${requiredValues.join(' or ')}`;
    } else if (requirement.nonCompliantIf === 'notIncludes' && !requiredValues.includes(strValue)) {
      status = 'NON_COMPLIANT';
      gap = `Your answer: ${strValue} | Required: ${requiredValues.join(' or ')}`;
    } else if (requirement.atRiskIf === 'notEquals' && !requiredValues.includes(strValue)) {
      status = 'AT_RISK';
      gap = `Your answer: ${strValue} | Recommended: ${requiredValues.join(' or ')}`;
    } else if (requirement.atRiskIf === 'notIncludes' && !requiredValues.includes(strValue)) {
      status = 'AT_RISK';
      gap = `Your answer: ${strValue} | Recommended: ${requiredValues.join(' or ')}`;
    }

    return {
      status,
      userValue: strValue,
      requiredValue: requirement.requiredValue || null,
      gap,
      citation: requirement.citation,
      description: requirement.description,
    };
  }

  // Handle multi-select (checkboxes)
  if (question.type === 'multi') {
    const selectedValues = Array.isArray(answer) ? answer : [];
    const requiredItems = requirement.requiredIncludes || [];

    if (requiredItems.length > 0) {
      // Check if all required items are selected
      const missingItems = requiredItems.filter(item => !selectedValues.includes(item));

      if (missingItems.length > 0) {
        // Some required items are missing
        status = 'NON_COMPLIANT';
        gap = `Missing required: ${missingItems.join(', ')}`;
      } else {
        status = 'COMPLIANT';
      }

      return {
        status,
        userValue: selectedValues,
        requiredValue: requiredItems,
        gap,
        citation: requirement.citation,
        description: requirement.description,
      };
    }

    // No required items specified - just check if any are selected
    if (selectedValues.length === 0) {
      return {
        status: 'UNKNOWN',
        userValue: selectedValues,
        requiredValue: null,
        citation: requirement.citation,
        description: requirement.description,
      };
    }
  }

  return {
    status: 'COMPLIANT',
    userValue: answer,
    requiredValue: null,
    citation: requirement.citation,
    description: requirement.description,
  };
}

// ============================================
// UI COMPONENTS
// ============================================

interface StatusBadgeProps {
  status: ComplianceStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = {
    COMPLIANT: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Compliant' },
    AT_RISK: { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertTriangle, label: 'At Risk' },
    NON_COMPLIANT: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Non-Compliant' },
    UNKNOWN: { bg: 'bg-gray-100', text: 'text-gray-500', icon: Scale, label: 'Not Answered' },
    NOT_APPLICABLE: { bg: 'bg-gray-100', text: 'text-gray-500', icon: Info, label: 'N/A' },
  };

  const c = config[status];
  const Icon = c.icon;

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium', c.bg, c.text)}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

interface GapDisplayProps {
  evaluation: EvaluationResult;
}

function GapDisplay({ evaluation }: GapDisplayProps) {
  if (evaluation.status === 'COMPLIANT' || evaluation.status === 'UNKNOWN') return null;

  const bgColor = evaluation.status === 'NON_COMPLIANT' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
  const textColor = evaluation.status === 'NON_COMPLIANT' ? 'text-red-800' : 'text-amber-800';
  const subTextColor = evaluation.status === 'NON_COMPLIANT' ? 'text-red-700' : 'text-amber-700';

  return (
    <div className={cn('mt-3 p-3 rounded-lg border', bgColor)}>
      {evaluation.gap && (
        <p className={cn('text-sm font-medium', textColor)}>{evaluation.gap}</p>
      )}
      {evaluation.citation && (
        <p className={cn('text-xs mt-1', subTextColor)}>
          <strong>Citation:</strong> {evaluation.citation}
        </p>
      )}
      {evaluation.description && (
        <p className={cn('text-xs mt-1', subTextColor)}>{evaluation.description}</p>
      )}
    </div>
  );
}

interface QuestionCardProps {
  question: DiagnosticQuestion;
  answer: string | number | string[] | null;
  evaluation: EvaluationResult;
  onChange: (value: string | number | string[] | null) => void;
  onFocus: () => void;
  questionNumber: number;
  stateCode: string;
  isExpanded: boolean;
}

function QuestionCard({ question, answer, evaluation, onChange, onFocus, questionNumber, stateCode, isExpanded }: QuestionCardProps) {
  const requirement = question.requirements[stateCode] ||
    question.requirements['DEFAULT'] ||
    question.defaultRequirement;

  // Show state-specific badge for state-specific questions
  const isStateSpecific = question.applicableStates && question.applicableStates.length > 0;

  return (
    <div className="p-5 border border-gray-200 rounded-lg bg-white">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-7 h-7 bg-teal-100 rounded-full flex items-center justify-center text-sm font-medium text-teal-700">
            {questionNumber}
          </span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-gray-900 font-medium">{question.question}</p>
              {isStateSpecific && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                  {stateCode} only
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Measuring: {question.extractionPrompt}
            </p>
          </div>
        </div>
        <StatusBadge status={evaluation.status} />
      </div>

      {/* Requirement Display - only show when expanded */}
      {isExpanded && requirement && (
        <div className="ml-10 mb-4 p-2 bg-gray-50 rounded text-xs text-gray-600">
          <strong>{stateCode} Requirement:</strong> {requirement.description}
          <span className="text-gray-400 ml-2">({requirement.citation})</span>
        </div>
      )}

      {/* Input */}
      <div className="ml-10">
        {question.type === 'number' && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={answer !== null ? answer : ''}
              onChange={e => onChange(e.target.value ? parseFloat(e.target.value) : null)}
              onFocus={onFocus}
              placeholder={question.placeholder}
              min={0}
              className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
            {question.unit && <span className="text-gray-500">{question.unit}</span>}
          </div>
        )}

        {question.type === 'yes_no' && (
          <div className="flex gap-3">
            {[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ].map(opt => (
              <label
                key={opt.value}
                onClick={onFocus}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all',
                  answer === opt.value
                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={opt.value}
                  checked={answer === opt.value}
                  onChange={() => onChange(opt.value)}
                  className="sr-only"
                />
                <span className="font-medium">{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'single' && question.options && (
          <div className="space-y-2">
            {question.options.map(opt => (
              <label
                key={opt.value}
                onClick={onFocus}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                  answer === opt.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                )}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={opt.value}
                  checked={answer === opt.value}
                  onChange={() => onChange(opt.value)}
                  className="w-4 h-4 accent-teal-600"
                />
                <span className={answer === opt.value ? 'text-teal-700 font-medium' : 'text-gray-700'}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'multi' && question.options && (
          <div className="space-y-2">
            {question.options.map(opt => {
              const selectedValues = Array.isArray(answer) ? answer : [];
              const isChecked = selectedValues.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  onClick={onFocus}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                    isChecked
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <input
                    type="checkbox"
                    value={opt.value}
                    checked={isChecked}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange([...selectedValues, opt.value]);
                      } else {
                        onChange(selectedValues.filter(v => v !== opt.value));
                      }
                    }}
                    className="w-4 h-4 accent-teal-600 rounded"
                  />
                  <span className={isChecked ? 'text-teal-700 font-medium' : 'text-gray-700'}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Gap Display - only show when expanded */}
      {isExpanded && (
        <div className="ml-10">
          <GapDisplay evaluation={evaluation} />
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

interface DiagnosticTotals {
  total: number;
  answered: number;
  compliant: number;
  atRisk: number;
  nonCompliant: number;
}

interface OnboardingDiagnosticProps {
  stateCode: string;
  answers: Record<string, string | string[] | number | null>;
  onChange: (questionId: string, value: string | string[] | number | null) => void;
  onProgressChange?: (totals: DiagnosticTotals) => void;
}

export type { DiagnosticTotals };

export function OnboardingDiagnostic({
  stateCode,
  answers,
  onChange,
  onProgressChange,
}: OnboardingDiagnosticProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);

  // Generate state-specific diagnostic sections based on selected state
  const dynamicSections = useMemo(() => {
    return generateStateSpecificSections(stateCode);
  }, [stateCode]);

  // Build a map of questionId -> sectionId for auto-expand
  const questionToSection = useMemo(() => {
    const map: Record<string, string> = {};
    for (const section of dynamicSections) {
      for (const question of section.questions) {
        map[question.id] = section.id;
      }
    }
    return map;
  }, [dynamicSections]);

  // Wrapped onChange that also expands the section and collapses previous question
  const handleAnswerChange = (questionId: string, value: string | string[] | number | null) => {
    onChange(questionId, value);

    // Auto-expand the section containing this question (accordion style)
    const sectionId = questionToSection[questionId];
    if (sectionId) {
      setExpandedSections(new Set([sectionId]));
    }

    // Expand only this question's details, collapse others
    setExpandedQuestionId(questionId);
  };

  // Calculate visible sections, questions, and evaluations
  const { visibleSections, evaluations, sectionStats, totals } = useMemo(() => {
    const sections: DiagnosticSection[] = [];
    const evals: Record<string, EvaluationResult> = {};
    const stats: Record<string, { total: number; compliant: number; atRisk: number; nonCompliant: number }> = {};
    let totalQ = 0, compliantQ = 0, atRiskQ = 0, nonCompliantQ = 0, answeredQ = 0;

    for (const section of dynamicSections) {
      if (!isSectionVisible(section, stateCode)) continue;

      let sectionTotal = 0;
      let sectionCompliant = 0;
      let sectionAtRisk = 0;
      let sectionNonCompliant = 0;
      let skipSection = false;

      const visibleQuestions: DiagnosticQuestion[] = [];

      for (const question of section.questions) {
        // Check skip condition from previous questions
        if (skipSection) continue;

        // Check if question should be visible
        if (!isQuestionVisible(question, stateCode, answers)) continue;

        visibleQuestions.push(question);
        sectionTotal++;
        totalQ++;

        // Evaluate
        const evaluation = evaluateQuestion(question, answers[question.id] ?? null, stateCode);
        evals[question.id] = evaluation;

        if (evaluation.status === 'COMPLIANT') {
          sectionCompliant++;
          compliantQ++;
          answeredQ++;
        } else if (evaluation.status === 'AT_RISK') {
          sectionAtRisk++;
          atRiskQ++;
          answeredQ++;
        } else if (evaluation.status === 'NON_COMPLIANT') {
          sectionNonCompliant++;
          nonCompliantQ++;
          answeredQ++;
        }

        // Check if we should skip rest of section
        if (question.skipSectionIf && answers[question.id]) {
          const skipValues = Array.isArray(question.skipSectionIf.value)
            ? question.skipSectionIf.value
            : [question.skipSectionIf.value];
          if (skipValues.includes(answers[question.id] as string)) {
            skipSection = true;
          }
        }
      }

      if (visibleQuestions.length > 0) {
        sections.push({ ...section, questions: visibleQuestions });
        stats[section.id] = {
          total: sectionTotal,
          compliant: sectionCompliant,
          atRisk: sectionAtRisk,
          nonCompliant: sectionNonCompliant,
        };
      }
    }

    return {
      visibleSections: sections,
      evaluations: evals,
      sectionStats: stats,
      totals: { total: totalQ, compliant: compliantQ, atRisk: atRiskQ, nonCompliant: nonCompliantQ, answered: answeredQ },
    };
  }, [stateCode, answers, dynamicSections]);

  // Auto-expand first section on mount
  useMemo(() => {
    if (visibleSections.length > 0 && expandedSections.size === 0) {
      setExpandedSections(new Set([visibleSections[0].id]));
    }
  }, [visibleSections, expandedSections.size]);

  // Report progress to parent
  useEffect(() => {
    if (onProgressChange) {
      onProgressChange(totals);
    }
  }, [totals, onProgressChange]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      // Accordion behavior: only one section open at a time
      if (prev.has(sectionId)) {
        // Clicking the open section closes it
        return new Set();
      } else {
        // Clicking a different section opens only that one
        return new Set([sectionId]);
      }
    });
  };

  const isNoLicenseState = NO_LICENSE_STATES.has(stateCode);

  return (
    <div className="space-y-6">
      {/* Summary Bar */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              Diagnostic for <strong>{stateCode}</strong>
            </span>
            {isNoLicenseState && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                No State License Required
              </span>
            )}
          </div>
          <span className="text-sm text-gray-500">
            {totals.answered} of {totals.total} questions
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
          <div
            className="bg-teal-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${totals.total > 0 ? (totals.answered / totals.total) * 100 : 0}%` }}
          />
        </div>
        <div className="flex items-center gap-6 text-sm">
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle className="w-4 h-4" />
            {totals.compliant} Compliant
          </span>
          <span className="flex items-center gap-1 text-amber-600">
            <AlertTriangle className="w-4 h-4" />
            {totals.atRisk} At Risk
          </span>
          <span className="flex items-center gap-1 text-red-600">
            <XCircle className="w-4 h-4" />
            {totals.nonCompliant} Non-Compliant
          </span>
        </div>
      </div>

      {/* Sections */}
      {visibleSections.map(section => {
        const isExpanded = expandedSections.has(section.id);
        const stats = sectionStats[section.id];
        const Icon = section.icon;
        const hasIssues = stats.nonCompliant > 0 || stats.atRisk > 0;
        const isComplete = stats.compliant + stats.atRisk + stats.nonCompliant === stats.total && stats.total > 0;

        return (
          <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Section Header */}
            <button
              onClick={() => toggleSection(section.id)}
              className={cn(
                'w-full flex items-center justify-between p-4 text-left transition-colors',
                isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  stats.nonCompliant > 0 ? 'bg-red-100' :
                    stats.atRisk > 0 ? 'bg-amber-100' :
                      isComplete ? 'bg-green-100' : 'bg-teal-100'
                )}>
                  {stats.nonCompliant > 0 ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : stats.atRisk > 0 ? (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  ) : isComplete ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <Icon className="w-5 h-5 text-teal-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{section.title}</h3>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {stats.compliant + stats.atRisk + stats.nonCompliant}/{stats.total}
                </span>
                {hasIssues && (
                  <span className="text-xs font-medium text-red-600">
                    {stats.nonCompliant} issues
                  </span>
                )}
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </button>

            {/* Section Content */}
            {isExpanded && (
              <div className="p-4 space-y-4 bg-gray-50 border-t border-gray-200">
                {section.questions.map((question, index) => (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    answer={answers[question.id] ?? null}
                    evaluation={evaluations[question.id]}
                    onChange={(value) => handleAnswerChange(question.id, value)}
                    onFocus={() => setExpandedQuestionId(question.id)}
                    questionNumber={index + 1}
                    stateCode={stateCode}
                    isExpanded={expandedQuestionId === question.id}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Final Summary */}
      {totals.answered === totals.total && totals.total > 0 && (
        <div className={cn(
          'p-4 rounded-lg border',
          totals.nonCompliant > 0 ? 'bg-red-50 border-red-200' :
            totals.atRisk > 0 ? 'bg-amber-50 border-amber-200' :
              'bg-green-50 border-green-200'
        )}>
          <div className="flex items-start gap-3">
            {totals.nonCompliant > 0 ? (
              <XCircle className="w-6 h-6 text-red-500" />
            ) : totals.atRisk > 0 ? (
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green-500" />
            )}
            <div>
              <h3 className={cn(
                'font-semibold',
                totals.nonCompliant > 0 ? 'text-red-800' :
                  totals.atRisk > 0 ? 'text-amber-800' :
                    'text-green-800'
              )}>
                {totals.nonCompliant > 0
                  ? `${totals.nonCompliant} Compliance Gap${totals.nonCompliant === 1 ? '' : 's'} Identified`
                  : totals.atRisk > 0
                    ? `${totals.atRisk} Area${totals.atRisk === 1 ? '' : 's'} Need Attention`
                    : 'No Immediate Compliance Gaps'
                }
              </h3>
              <p className={cn(
                'text-sm mt-1',
                totals.nonCompliant > 0 ? 'text-red-700' :
                  totals.atRisk > 0 ? 'text-amber-700' :
                    'text-green-700'
              )}>
                {totals.nonCompliant > 0
                  ? 'Address non-compliant items before your next survey. Continue to the detailed checklist.'
                  : totals.atRisk > 0
                    ? 'Review at-risk areas to strengthen compliance. Continue to the checklist.'
                    : 'Your initial assessment looks good. Continue to the detailed checklist.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OnboardingDiagnostic;
